import { t } from "i18next";
import { AlertTriangle, CloudOff, HardDrive } from "lucide-react";
import type { JSX, ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface OfflineRecordingNotices {
  /** The browser reports no connection. */
  offline: boolean;
  /** Saving to this device failed (full or blocked storage). */
  storageFailed: boolean;
  /** Free browser storage is low (bytes left), or null. */
  lowStorageBytes: number | null;
  /** The recording is close to the owned upload limit. */
  nearUploadLimit: boolean;
}

const Notice = ({
  tone,
  icon,
  children,
  testId,
}: {
  tone: "offline" | "warning" | "error";
  icon: ReactNode;
  children: ReactNode;
  testId: string;
}): JSX.Element => (
  <div
    className={cn(
      "flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left text-sm leading-5",
      tone === "offline" &&
        "border-warning/40 bg-warning/10 text-foreground dark:bg-warning/15",
      tone === "warning" &&
        "border-warning/40 bg-warning/10 text-foreground dark:bg-warning/15",
      tone === "error" &&
        "border-error/40 bg-error/10 text-foreground dark:bg-error/15",
    )}
    data-testid={testId}
  >
    <span
      aria-hidden="true"
      className={cn(
        "mt-0.5 shrink-0",
        tone === "error" ? "text-error" : "text-warning",
      )}
    >
      {icon}
    </span>
    <p className="min-w-0 flex-1">{children}</p>
  </div>
);

/**
 * Non-blocking notices shown above the recorder. Recording never stops
 * because of them; they only say where the audio is kept.
 */
export const OfflineRecordingBanner = ({
  offline,
  storageFailed,
  lowStorageBytes,
  nearUploadLimit,
  className,
}: OfflineRecordingNotices & { className?: string }): JSX.Element | null => {
  if (
    !offline &&
    !storageFailed &&
    lowStorageBytes === null &&
    !nearUploadLimit
  )
    return null;
  return (
    <div
      aria-live="polite"
      className={cn("flex w-full flex-col gap-2", className)}
      role="status"
    >
      {offline && !storageFailed && (
        <Notice
          icon={<CloudOff className="h-4 w-4" />}
          testId="offline-recording-banner"
          tone="offline"
        >
          <span className="font-semibold">
            {t("offlineRecording.banner.offlineTitle", "Offline:")}
          </span>{" "}
          {t(
            "offlineRecording.banner.offline",
            "recording is being saved on this device.",
          )}
        </Notice>
      )}
      {storageFailed && (
        <Notice
          icon={<HardDrive className="h-4 w-4" />}
          testId="offline-recording-storage-error"
          tone="error"
        >
          {t(
            "offlineRecording.banner.storageFailed",
            "This device has no space left to save the recording. Recording continues in this tab only, so keep it open until you submit or download the audio.",
          )}
        </Notice>
      )}
      {!storageFailed && lowStorageBytes !== null && (
        <Notice
          icon={<HardDrive className="h-4 w-4" />}
          testId="offline-recording-low-storage"
          tone="warning"
        >
          {t(
            "offlineRecording.banner.lowStorage",
            "Low storage on this device ({{size}} MB free). Recording continues, but free up space if this visit is long.",
            { size: Math.floor(lowStorageBytes / (1024 * 1024)) },
          )}
        </Notice>
      )}
      {nearUploadLimit && (
        <Notice
          icon={<AlertTriangle className="h-4 w-4" />}
          testId="offline-recording-size-warning"
          tone="warning"
        >
          {t(
            "offlineRecording.banner.nearLimit",
            "This recording is close to the 200 MB upload limit (about 19 hours). Stop and save soon; a longer recording will have to be downloaded.",
          )}
        </Notice>
      )}
    </div>
  );
};
